import {
  ASSET_LABEL_MAX_LENGTH,
  type GeneratedAsset,
  QR_CREDIT_COST,
  type QrErrorCorrection,
} from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Download, QrCode } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGenerateQr } from '~/features/ai-studio/hooks'
import {
  CreditCostNote,
  CreditCostTag,
  useCreditCost,
} from '~/features/shared/components/credit-cost'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'

const SIZES = [512, 1024, 2048]

const MARGINS: { value: number; label: () => string }[] = [
  { value: 0, label: m.qrMarginNone },
  { value: 2, label: m.qrMarginStandard },
  { value: 4, label: m.qrMarginWide },
]

const ERROR_CORRECTION_LEVELS: { value: QrErrorCorrection; label: () => string }[] = [
  { value: 'L', label: m.qrErrorCorrectionL },
  { value: 'M', label: m.qrErrorCorrectionM },
  { value: 'Q', label: m.qrErrorCorrectionQ },
  { value: 'H', label: m.qrErrorCorrectionH },
]

interface QrGeneratorProps {
  /** Fixes the encoded payload - used when the code belongs to an event or place. */
  presetData?: string
  /** Names the entity the code belongs to; becomes part of the stored asset title. */
  presetLabel?: string
  /** Lets an embedding dialog stay open while a paid generation is in flight. */
  onBusyChange?: (busy: boolean) => void
}

export function QrGenerator({ presetData, presetLabel, onBusyChange }: QrGeneratorProps) {
  const idPrefix = useId()
  const resultRef = useRef<HTMLDivElement>(null)
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const generate = useGenerateQr(businessId)

  const [freeData, setFreeData] = useState('')
  const [foreground, setForeground] = useState('#000000')
  const [background, setBackground] = useState('#ffffff')
  const [size, setSize] = useState(1024)
  const [margin, setMargin] = useState(2)
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState<QrErrorCorrection>('M')
  const [logo, setLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [result, setResult] = useState<GeneratedAsset | null>(null)

  const isEmbedded = presetData !== undefined
  const data = presetData ?? freeData
  const { state: creditState } = useCreditCost(businessId, QR_CREDIT_COST)
  const canGenerate =
    creditState !== 'short' && data.trim().length > 0 && !!businessId && !generate.isPending
  // A centre logo occludes modules, so the service forces the highest recovery
  // level - mirror that here instead of leaving a stale selection highlighted.
  const effectiveLevel: QrErrorCorrection = logo ? 'H' : errorCorrectionLevel

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview)
    }
  }, [logoPreview])

  useEffect(() => {
    onBusyChange?.(generate.isPending)
  }, [generate.isPending, onBusyChange])

  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ block: 'nearest' })
  }, [result])

  function loadLogo(picked: File | null) {
    setLogo(picked)
    setLogoPreview(picked ? URL.createObjectURL(picked) : null)
  }

  async function handleGenerate() {
    setResult(null)
    try {
      const res = await generate.mutateAsync({
        params: {
          data: data.trim(),
          foreground,
          background,
          size,
          margin,
          errorCorrectionLevel: effectiveLevel,
          label: presetLabel?.slice(0, ASSET_LABEL_MAX_LENGTH),
        },
        logo,
      })
      setResult(res.asset)
      toast.success(m.qrDone())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-qr-data`}>{m.qrData()}</Label>
        <Input
          id={`${idPrefix}-qr-data`}
          value={data}
          readOnly={isEmbedded}
          className="read-only:cursor-default read-only:bg-muted/40 read-only:text-muted-foreground"
          placeholder={m.qrDataPlaceholder()}
          onChange={(e) => setFreeData(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-qr-foreground`}>{m.qrForeground()}</Label>
          <Input
            id={`${idPrefix}-qr-foreground`}
            type="color"
            value={foreground}
            onChange={(e) => setForeground(e.target.value)}
            className="h-9 w-16 p-1"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-qr-background`}>{m.qrBackground()}</Label>
          <Input
            id={`${idPrefix}-qr-background`}
            type="color"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            className="h-9 w-16 p-1"
          />
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-medium">{m.qrSize()}</legend>
          <div className="flex gap-2">
            {SIZES.map((value) => (
              <Button
                key={value}
                type="button"
                size="sm"
                aria-pressed={size === value}
                variant={size === value ? 'default' : 'outline'}
                onClick={() => setSize(value)}
              >
                {value}
              </Button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-sm font-medium">{m.qrMargin()}</legend>
          <div className="flex gap-2">
            {MARGINS.map(({ value, label }) => (
              <Button
                key={value}
                type="button"
                size="sm"
                aria-pressed={margin === value}
                variant={margin === value ? 'default' : 'outline'}
                onClick={() => setMargin(value)}
              >
                {label()}
              </Button>
            ))}
          </div>
        </fieldset>
      </div>

      <fieldset aria-describedby={`${idPrefix}-qr-ec-hint`}>
        <legend className="mb-2 text-sm font-medium">{m.qrErrorCorrection()}</legend>
        <div className="flex flex-wrap gap-2">
          {ERROR_CORRECTION_LEVELS.map(({ value, label }) => (
            <Button
              key={value}
              type="button"
              size="sm"
              disabled={!!logo}
              aria-pressed={effectiveLevel === value}
              variant={effectiveLevel === value ? 'default' : 'outline'}
              onClick={() => setErrorCorrectionLevel(value)}
            >
              {label()}
            </Button>
          ))}
        </div>
        <p id={`${idPrefix}-qr-ec-hint`} className="mt-2 text-xs text-muted-foreground">
          {logo ? m.qrErrorCorrectionLogoHint() : m.qrErrorCorrectionHint()}
        </p>
      </fieldset>

      <div className="space-y-2">
        <Label>{m.qrLogo()}</Label>
        <ImageUploadField
          file={logo}
          preview={logoPreview}
          onSelect={loadLogo}
          showLibrary={!isEmbedded}
          compact
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          <CreditCostNote businessId={businessId} cost={QR_CREDIT_COST} />
        </div>
        <Button disabled={!canGenerate} onClick={handleGenerate}>
          {generate.isPending ? (
            <PulsifyLoader className="size-5" tone="on-brand" />
          ) : (
            <QrCode className="h-4 w-4" />
          )}
          {generate.isPending ? m.qrGenerating() : m.qrGenerate()}
          {!generate.isPending && <CreditCostTag cost={QR_CREDIT_COST} />}
        </Button>
      </div>

      {result && (
        <div ref={resultRef} className="space-y-3 border-t pt-5">
          <div className="flex items-center justify-between">
            <p className="font-medium">{m.formatConverterResults()}</p>
            <Button asChild variant="link" size="sm">
              <Link to="/dashboard/media">{m.mediaLibrary()}</Link>
            </Button>
          </div>
          <div className="relative inline-block overflow-hidden rounded-lg bg-muted">
            <img src={result.url} alt={m.qrResultAlt()} className="max-h-64 object-contain" />
            <Button asChild size="icon" variant="secondary" className="absolute right-1 bottom-1">
              <a
                href={result.url}
                download
                target="_blank"
                rel="noreferrer"
                aria-label={m.qrDownload()}
              >
                <Download className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
